import { Link as RouterLink, NavLink as RouterNavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

const linkStyle = css`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 8px 14px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #d8d4cd;
  font: inherit;
  font-size: .9375rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  position: relative;
  transition: color .18s ease;
  &::after {
    content: '';
    position: absolute;
    left: 14px;
    right: 14px;
    bottom: 3px;
    height: 2px;
    background: #f5d28a;
    opacity: 0;
    transform: scaleX(.2);
    transition: opacity .18s ease, transform .18s ease;
  }
  &:hover, &:focus-visible, &.active { color: #fff; }
  &:hover::after, &:focus-visible::after, &.active::after { opacity: 1; transform: scaleX(1); }
  &:hover svg, &:focus-visible svg, &.active svg { color: #f5d28a; opacity: 1; }
  &:focus-visible { outline: 2px solid #f5d28a; outline-offset: 2px; }
  svg { flex: 0 0 auto; width: 17px; height: 17px; opacity: .78; transition: color .18s ease, opacity .18s ease; }
  @media (prefers-reduced-motion: reduce) { &, svg, &::after { transition: none; } }
`;

export const Nav = styled.nav`
  position: fixed;
  inset: 0 0 auto;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 56px;
  padding: 0 clamp(12px, 2.5vw, 36px);
  box-sizing: border-box;
  border-bottom: 1px solid #504a40;
  background: #282725;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  .nav-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
  @media (max-width: 1000px) { gap: 10px; }
`;

export const NavBtnLink = styled(RouterLink)`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: 44px;
  margin-right: 8px;
  border-radius: 4px;
  img { width: auto; height: 34px; object-fit: contain; }
  &:focus-visible { outline: 2px solid #f5d28a; outline-offset: 2px; }
  @media (max-width: 1000px) { margin-right: auto; }
`;

export const NavLink = styled(RouterNavLink)`${linkStyle}`;
export const ExternalLink = styled.a`
  ${linkStyle}
  margin-left: 8px;
  border-left: 1px solid #504a40;
  border-radius: 0;
`;
export const MenuButton = styled.button`
  ${linkStyle}
  display: none;
  svg { width: 28px; height: 28px; }
  @media (max-width: 1000px) { display: inline-flex; justify-content: center; min-width: 44px; padding: 8px 10px; }
`;
export const ResourceButton = styled.button`${linkStyle}`;
export const ResourceGroup = styled.div`
  position: relative;
  .nav-resource-heading { display: none; }
`;
export const ResourceMenu = styled.div`
  display: ${({ $isOpen }) => $isOpen ? 'grid' : 'none'};
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 260px;
  padding: 8px;
  border: 1px solid #63553b;
  border-radius: 8px;
  background: #282725;
  box-shadow: 0 12px 28px rgba(0, 0, 0, .3);
  ${NavLink} { display: grid; gap: 2px; white-space: normal; }
  ${NavLink} > span { display: inline-flex; align-items: center; gap: 6px; }
  small { color: #c2bbae; font-size: .75rem; font-weight: 400; }
`;
export const NavMenu = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  .nav-primary { display: flex; align-items: center; }
  @media (max-width: 1000px) {
    display: ${({ $isOpen }) => $isOpen ? 'grid' : 'none'};
    position: absolute;
    top: 56px;
    left: 0;
    right: 0;
    gap: 12px;
    max-height: calc(100dvh - 56px - var(--local-preview-bottom, 0px));
    overflow-y: auto;
    overscroll-behavior: contain;
    box-sizing: border-box;
    padding: 16px max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
    margin: 0;
    border-bottom: 1px solid #63553b;
    background: #282725;
    box-shadow: 0 14px 28px rgba(0, 0, 0, .3);
    .nav-primary { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 12px; }
    ${ResourceButton} { display: none; }
    ${ResourceGroup} { border-top: 1px solid #504a40; padding-top: 10px; }
    .nav-resource-heading { display: block; margin: 0 14px 4px; color: #c2bbae; font-size: .75rem; }
    ${ResourceMenu} { display: grid; position: static; min-width: 0; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; padding: 0; border: 0; background: transparent; box-shadow: none; }
    ${ExternalLink} { margin-left: 0; padding-top: 12px; border-left: 0; border-top: 1px solid #504a40; }
  }
`;
